import React, { useState } from 'react'
import { colors } from './fieldTheme'
import { formatDateTime } from './fieldDates'
import { guestName } from './fieldStays'
import { plural } from './fieldReference'
import { commentTile, photoTile } from './fieldSummary'
import { duringCommentItems, afterCommentItems, duringPhotoItems, afterPhotoItems, commentChoices } from './fieldNotes'
import { FieldPhotoSheet, FieldCommentSheet } from './FieldSheets'
import FieldInterventionSheet from './FieldInterventionSheet'
import { interventionTile, interventionSummary } from './fieldInterventions'
import { CheckIcon, CameraIcon, CommentIcon, WrenchIcon, HistoryIcon } from './FieldIcons'
import { FLOW_TYPES, PrimaryButton, ErrorNote, LinkButton } from './FieldUI'
import { UndoToast } from './FieldComments'

// Suite à donner, après l'enregistrement (canvas W16 et W16b, décisions 30, 34 et 48) ; les tuiles
// Commentaire et Photos ouvrent les mêmes volets que dans un thème (W20, W21).
// tuiles Commentaire, Photos et Créer une intervention, toutes facultatives. Grille de 2 colonnes,
// le dernier bloc d'une grille impaire prend toute la largeur (même règle que les thèmes).
// « Créer une intervention » : choix Réassort / Réparation puis formulaire allégé (décisions 30 et 42) ;
// les problèmes signalés pendant l'inventaire sont rappelés en tête, contre les doublons (décision 43).
// Pas de <nav> : Materialize lui impose un fond rouge et une hauteur de 56 px.

function SavedBanner({ type, pitch, saved })
{
	return (
		<div role="status" style={{ margin: '24px 20px 0', borderRadius: 14, background: '#DCFCE7', color: '#14532D', display: 'flex', alignItems: 'center', gap: 12, padding: 20 }}>
			<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
				<circle cx="12" cy="12" r="9.5" />
				<path d="M7.5 12.5l3 3 6-6" />
			</svg>
			<span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 19, fontWeight: 800 }}>Inventaire enregistré</span>
				<span style={{ fontSize: 15, fontWeight: 600 }}>{pitch.location + ' · ' + FLOW_TYPES[type].short}</span>
				<span style={{ fontSize: 15, fontWeight: 600 }}>{formatDateTime(saved.create_time)}</span>
			</span>
		</div>
	)
}

// Tuile de Suite à donner, reprise telle quelle par la fiche d'un emplacement du tour (M6b, M6c)
export function Tile({ icon, color, title, subtitle, done, wide, disabled, onClick })
{
	return (
		<button
			type="button"
			className="field-press"
			disabled={disabled}
			onClick={onClick}
			style={{
				backgroundImage: 'none',
				backgroundColor: disabled ? '#E9ECF1' : colors.surface,
				position: 'relative',
				gridColumn: wide ? '1 / -1' : 'auto',
				minWidth: 0,
				minHeight: 180,
				border: done ? '3px solid ' + colors.primary : '2px solid ' + colors.border,
				borderRadius: 18,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 10,
				padding: '20px 12px',
				textAlign: 'center',
				color: disabled ? '#5B6577' : colors.text,
				fontFamily: 'inherit',
				cursor: disabled ? 'not-allowed' : 'pointer'
			}}
		>
			{done && (
				<span aria-hidden="true" style={{ position: 'absolute', top: 8, right: 8, width: 34, height: 34, borderRadius: 999, background: colors.primary, border: '2px solid #FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<CheckIcon size={20} color="#FFFFFF" />
				</span>
			)}
			<span aria-hidden="true" style={{ width: 80, height: 80, borderRadius: 999, background: disabled ? '#9AA3B8' : color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
				{icon}
			</span>
			<span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
				<span lang="fr" style={{ fontSize: 19, fontWeight: 800, overflowWrap: 'normal', wordBreak: 'normal' }}>{title}</span>
				<span style={{ fontSize: 15, fontWeight: 600, color: disabled ? '#5B6577' : colors.textSecondary }}>{subtitle}</span>
			</span>
		</button>
	)
}

export default function FieldFollowUp({ type, pitch, stay, client, saved, themes, notes, user, now, toast, undoError, interventions, missing, restockOffered, loadUsers, onUndo, onDismissToast, onAddComment, onUpdateComment, onAddPhotos, onCreateIntervention, onFinish, onActivity })
{
	const [sheet, setSheet] = useState(null)
	const during = duringCommentItems(themes, notes)
	const after = afterCommentItems(themes, notes)
	const savedPhotos = duringPhotoItems(themes, notes).concat(afterPhotoItems(notes))
	const duringPhotoCount = duringPhotoItems(themes, notes).length
	const comments = commentTile(during.length, after.length)
	const photos = photoTile(duringPhotoCount, afterPhotoItems(notes).length)
	const created = interventionTile(interventions.filter(item => !item.during))
	const reported = interventions.filter(item => item.during)
	const name = guestName(client)
	const identity = stay ? [name, 'séjour n° ' + stay.stay_number].filter(Boolean).join(', ') : null

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<UndoToast toast={toast} onUndo={onUndo} onDismiss={onDismissToast} />
			{undoError && <div style={{ padding: '8px 20px 0' }}><ErrorNote>L'annulation n'a pas pu être enregistrée. Vérifiez la connexion, puis réessayez depuis la tuile concernée.</ErrorNote></div>}
			<SavedBanner type={type} pitch={pitch} saved={saved} />

			{reported.length > 0 && (
				<div style={{ padding: '24px 20px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
					<h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase' }}>Déjà signalé pendant l'inventaire</h2>
					{reported.map(item => (
						<div key={item.id} style={{ minHeight: 72, background: '#E8EBF5', borderRadius: 14, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
							<span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 999, background: '#8124FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><WrenchIcon size={26} /></span>
							<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
								<span style={{ fontSize: 17, fontWeight: 800, overflowWrap: 'break-word' }}>{item.doc.description}</span>
								<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{interventionSummary(item)}</span>
							</span>
						</div>
					))}
				</div>
			)}

			<div style={{ padding: '28px 20px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
				<h1 style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2, color: colors.text }}>Suite à donner</h1>
				{identity && <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{identity.charAt(0).toUpperCase() + identity.slice(1)}</span>}
			</div>
			<p style={{ padding: '20px 20px 12px', fontSize: 17, fontWeight: 600, color: colors.text }}>Tout est facultatif.</p>

			<div role="group" aria-label="Suite à donner" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '0 20px' }}>
				<Tile icon={<CommentIcon />} color="#0F766E" title="Commentaire" subtitle={comments.subtitle} done={comments.done} onClick={() => setSheet('comment')} />
				<Tile icon={<CameraIcon size={46} color="#FFFFFF" />} color="#B45309" title="Photos" subtitle={photos.subtitle} done={photos.done} onClick={() => setSheet('photo')} />
				<Tile icon={<WrenchIcon />} color="#8124FF" title="Créer une intervention" subtitle={created.subtitle} done={created.done} wide onClick={() => setSheet('intervention')} />
			</div>

			<div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8, padding: '24px 20px', paddingBottom: 'max(28px, env(safe-area-inset-bottom))' }}>
				{saved.simulated && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>Version d'essai : rien n'est écrit en base.</p>}
				<p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>Rien d'autre ? Terminez pour revenir à l'accueil.</p>
				<PrimaryButton onClick={onFinish}>Terminer</PrimaryButton>
				{onActivity && (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<LinkButton icon={<HistoryIcon />} onClick={onActivity}>Voir dans mon activité</LinkButton>
					</div>
				)}
			</div>

			{sheet === 'comment' && (
				<FieldCommentSheet
					title="Commentaire"
					listTitle="Déjà saisis"
					items={during.concat(after)}
					user={user}
					now={now}
					placeholder={'Ce que vous avez constaté sur ' + pitch.location}
					saveLabel="Enregistrer le commentaire"
					onSave={(text) => onAddComment(text).then(() => setSheet(null))}
					onUpdate={onUpdateComment}
					onClose={() => setSheet(null)}
				/>
			)}
			{sheet === 'intervention' && (
				<FieldInterventionSheet
					start="choice"
					singleScroll
					missing={missing}
					restockOffered={restockOffered}
					restockDone={interventions.some(item => item.kind === 'restock')}
					inventoryPhotos={savedPhotos}
					inventoryComments={commentChoices(during.concat(after).map(item => item.comment), user)}
					pitch={pitch}
					stay={stay}
					user={user}
					loadUsers={loadUsers}
					onCreate={(payload) => onCreateIntervention(payload).then(() => setSheet(null))}
					onClose={() => setSheet(null)}
				/>
			)}
			{sheet === 'photo' && (
				<FieldPhotoSheet
					title="Photos"
					subtitle={savedPhotos.length > 0 ? plural(savedPhotos.length, 'photo déjà déposée', 'photos déjà déposées') : null}
					saveLabel="Enregistrer les photos"
					user={user}
					existing={savedPhotos}
					onSave={(photos) => onAddPhotos(photos).then(() => setSheet(null))}
					onClose={() => setSheet(null)}
				/>
			)}
		</div>
	)
}
